/**
 * PDP quantity picker: <select> capped at purchasable max; syncs on variant change.
 * Stock + rules: .productVariantsQty only (never variant JSON inventory_quantity).
 * Caps use Admin stock from .productVariantsQty only on variant change.
 * Cart line qty is not subtracted in the picker (avoids false in_cart_max on every
 * option click); Shopify still validates on add-to-cart.
 */
(function () {
  const DEFAULT_MAX = 99;
  const HINT_HIDE_MS = 5000;

  let cartQtyByVariant = null;
  let cartFetchPromise = null;

  function normalizeVariantId(id) {
    if (id == null || id === "") return null;
    const s = String(id).trim();
    const gid = s.match(/ProductVariant\/(\d+)/i);
    const raw = gid ? gid[1] : s;
    if (/^\d+$/.test(raw)) return String(parseInt(raw, 10));
    return raw;
  }

  function variantOptionValues(variant) {
    if (!variant) return [];
    const out = [];
    if (variant.option1 != null && variant.option1 !== "") out.push(variant.option1);
    else if (variant.options?.[0]) out.push(variant.options[0]);
    if (variant.option2 != null && variant.option2 !== "")
      out.push(variant.option2);
    else if (variant.options?.[1]) out.push(variant.options[1]);
    if (variant.option3 != null && variant.option3 !== "")
      out.push(variant.option3);
    else if (variant.options?.[2]) out.push(variant.options[2]);
    return out;
  }

  function rowMatchesVariant(row, variant) {
    if (!row || !variant) return false;
    const opts = variantOptionValues(variant);
    if (!opts.length) return false;
    const rowOpts = [row.option1, row.option2, row.option3].filter(
      (v) => v != null && v !== ""
    );
    if (rowOpts.length !== opts.length) return false;
    for (let i = 0; i < opts.length; i++) {
      if (String(rowOpts[i]) !== String(opts[i])) return false;
    }
    return true;
  }

  function buildCartQtyMap(cart) {
    const map = {};
    if (!cart || !Array.isArray(cart.items)) return map;
    cart.items.forEach((item) => {
      if (item?.variant_id == null) return;
      const id = normalizeVariantId(item.variant_id);
      if (!id) return;
      map[id] = (map[id] || 0) + Number(item.quantity || 0);
    });
    return map;
  }

  function applyCartQtyCache(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    cartQtyByVariant = buildCartQtyMap(cart);
    return true;
  }

  function invalidateCartQtyCache() {
    cartQtyByVariant = null;
    cartFetchPromise = null;
  }

  function fetchCartVariantQuantities(force) {
    if (force) invalidateCartQtyCache();
    if (cartQtyByVariant) return Promise.resolve(cartQtyByVariant);
    if (cartFetchPromise) return cartFetchPromise;

    cartFetchPromise = fetch("/cart.js", { credentials: "same-origin" })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((cart) => {
        const map = buildCartQtyMap(cart);
        cartQtyByVariant = map;
        return map;
      })
      .catch(() => cartQtyByVariant || {})
      .finally(() => {
        cartFetchPromise = null;
      });

    return cartFetchPromise;
  }

  function getInCartQty(variantId) {
    const key = normalizeVariantId(variantId);
    if (!key || !cartQtyByVariant) return 0;
    const qty = cartQtyByVariant[key];
    return qty != null && !Number.isNaN(Number(qty)) ? Number(qty) : 0;
  }

  function clearProductFormErrors(root) {
    const scope = root || document;
    scope.querySelectorAll("product-form").forEach((form) => {
      if (typeof form.handleErrorMessage === "function") {
        form.handleErrorMessage();
      }
    });
    scope
      .querySelectorAll(".product-form__error-message-wrapper")
      .forEach((wrapper) => {
        wrapper.toggleAttribute("hidden", true);
        const msg = wrapper.querySelector(".product-form__error-message");
        if (msg) msg.textContent = "";
      });
  }

  function getVariantQtyRows(root) {
    if (!root) return [];
    const el =
      root.querySelector(".productVariantsQty") ||
      root.closest(".product__item-js")?.querySelector(".productVariantsQty");
    if (!el) return [];
    try {
      const rows = JSON.parse(el.textContent);
      return Array.isArray(rows) ? rows : [];
    } catch (_) {
      return [];
    }
  }

  function getPickerFormVariantId(pickerEl) {
    if (!pickerEl) return null;

    const select =
      pickerEl.querySelector?.('select[name="quantity"]') ||
      (pickerEl.tagName === "JM-QUANTITY-PICKER"
        ? pickerEl.querySelector("select")
        : null);

    const formAttr = select?.getAttribute("form");
    if (formAttr) {
      const form = document.getElementById(formAttr);
      const input = form?.querySelector('input[name="id"]');
      const val = input?.value;
      if (val && String(val).trim() !== "") return String(val).trim();
    }

    const scopedInput = pickerEl
      .closest("product-form")
      ?.querySelector(
        'form[data-type="add-to-cart-form"] input[name="id"], form input[name="id"]'
      );
    const scopedVal = scopedInput?.value;
    if (scopedVal && String(scopedVal).trim() !== "") return String(scopedVal).trim();

    return null;
  }

  function getQtyRow(root, variant, pickerEl) {
    const rows = getVariantQtyRows(root);
    if (!rows.length || !variant) return null;

    const variantKey = normalizeVariantId(variant.id);
    if (variantKey) {
      const byVariant = rows.find(
        (r) => normalizeVariantId(r.id) === variantKey
      );
      if (byVariant) return byVariant;

      const byFormId = normalizeVariantId(getPickerFormVariantId(pickerEl));
      if (byFormId) {
        const byForm = rows.find((r) => normalizeVariantId(r.id) === byFormId);
        if (byForm) return byForm;
      }

      const byOptions = rows.find((r) => rowMatchesVariant(r, variant));
      if (byOptions) return byOptions;
      return null;
    }

    const formId = normalizeVariantId(getPickerFormVariantId(pickerEl));
    if (formId) {
      return rows.find((r) => normalizeVariantId(r.id) === formId) || null;
    }

    return rows.find((r) => rowMatchesVariant(r, variant)) || null;
  }

  function resolveStockContext(variant, root, pickerEl) {
    const row = getQtyRow(root, variant, pickerEl);
    if (!row) {
      return {
        row: null,
        tracksInventory: false,
        stock: null,
        policy: "deny",
        ruleMin: 1,
        ruleMax: null,
        ruleStep: 1,
      };
    }

    const stock =
      row.qty != null && row.qty !== "" && !Number.isNaN(Number(row.qty))
        ? Number(row.qty)
        : null;
    const management =
      row.management != null ? String(row.management).toLowerCase() : "";
    const tracksInventory = management === "shopify";

    let ruleMax = null;
    if (
      row.rule_max != null &&
      row.rule_max !== "" &&
      !Number.isNaN(Number(row.rule_max))
    ) {
      ruleMax = Number(row.rule_max);
    }

    return {
      row,
      tracksInventory,
      stock,
      policy: row.policy || "deny",
      ruleMin:
        row.rule_min != null && !Number.isNaN(Number(row.rule_min))
          ? Number(row.rule_min)
          : 1,
      ruleMax,
      ruleStep:
        row.rule_increment != null && !Number.isNaN(Number(row.rule_increment))
          ? Number(row.rule_increment)
          : 1,
    };
  }

  function computeLimitsFromContext(ctx, options) {
    const opts = options || {};
    const inCart =
      Number(opts.inCartQty) > 0 ? Number(opts.inCartQty) : 0;
    const showCartMaxHint = opts.showCartMaxHint === true;

    const min = ctx.ruleMin > 0 ? ctx.ruleMin : 1;
    const step = ctx.ruleStep > 0 ? ctx.ruleStep : 1;
    const ruleMax = ctx.ruleMax;

    let inventoryCapped = false;
    let stockTotal = null;
    let purchasableMax = null;

    if (
      ctx.tracksInventory &&
      ctx.policy === "deny" &&
      ctx.stock != null &&
      !Number.isNaN(ctx.stock) &&
      ctx.stock > 0
    ) {
      stockTotal = ctx.stock;
      inventoryCapped = true;
      purchasableMax =
        inCart > 0 ? Math.max(0, ctx.stock - inCart) : ctx.stock;
    }

    const atCartMax =
      inCart > 0 &&
      stockTotal != null &&
      stockTotal > 0 &&
      inCart >= stockTotal;

    const nothingLeftToAdd =
      atCartMax ||
      (inCart > 0 &&
        stockTotal != null &&
        purchasableMax != null &&
        purchasableMax <= 0);

    if (nothingLeftToAdd) {
      return {
        min: 1,
        max: 1,
        step,
        inventoryCapped,
        ruleMax,
        stockMax: 0,
        stockTotal,
        inCart,
        cartFull: true,
        selectDisabled: true,
        showCartMaxHint,
      };
    }

    const caps = [DEFAULT_MAX];
    if (ruleMax != null && ruleMax > 0) caps.push(ruleMax);
    if (purchasableMax != null && purchasableMax > 0) caps.push(purchasableMax);

    let max = Math.min(...caps);
    if (max < min) max = min;

    return {
      min,
      max,
      step,
      inventoryCapped,
      ruleMax,
      stockMax: purchasableMax,
      stockTotal,
      inCart,
      cartFull: false,
      selectDisabled: false,
      showCartMaxHint: false,
    };
  }

  /** @deprecated Use resolveStockContext + computeLimitsFromContext */
  function enrichVariantForQuantity(variant, root, pickerEl) {
    if (!variant) return variant;
    const ctx = resolveStockContext(variant, root, pickerEl);
    if (!ctx.row) return variant;

    const merged = { ...variant };
    const rule = { ...(variant.quantity_rule || {}) };

    if (ctx.stock != null) merged.inventory_quantity = ctx.stock;
    if (ctx.tracksInventory) merged.inventory_management = "shopify";
    else if (ctx.row.management === null) merged.inventory_management = null;
    merged.inventory_policy = ctx.policy;
    rule.min = ctx.ruleMin;
    rule.increment = ctx.ruleStep;
    if (ctx.ruleMax != null) rule.max = ctx.ruleMax;
    merged.quantity_rule = rule;
    return merged;
  }

  function computeLimits(variant, options) {
    const ctx = resolveStockContext(variant, null, null);
    return computeLimitsFromContext(ctx, options);
  }

  function buildValues(min, max, step) {
    const values = [];
    for (let v = min; v <= max; v += step) {
      values.push(v);
      if (values.length > 500) break;
    }
    return values;
  }

  function limitMessage(limits) {
    const s = window.variantStrings || {};
    const countPlaceholder = /\{\{\s*count\s*\}\}|__COUNT__/g;

    if (limits.cartFull && limits.showCartMaxHint) {
      return s.quantityInCartMax || "";
    }

    const { max, inventoryCapped } = limits;
    if (max === 1) return s.quantityStockHintOne || "";
    if (inventoryCapped) {
      return (s.quantityStockHintFew || "").replace(
        countPlaceholder,
        String(max)
      );
    }
    return (s.quantityStockHintMax || "").replace(
      countPlaceholder,
      String(max)
    );
  }

  class JmQuantityPicker extends HTMLElement {
    constructor() {
      super();
      this.select = null;
      this._limits = { max: DEFAULT_MAX, inventoryCapped: false };
      this._hintTimer = null;
      this._changeEvent = new Event("change", { bubbles: true });
      this._pendingUpdate = null;
      this._applyingLimits = false;
    }

    getWrap() {
      return this.closest(".jm-quantity-picker-wrap");
    }

    getHintEl() {
      return this.getWrap()?.querySelector(".jm-quantity-picker__hint") || null;
    }

    getScopeRoot() {
      return (
        this.closest(".product__item-js") ||
        this.closest("sticky-add-cart")?.closest(".product__item-js")
      );
    }

    getActiveVariantIdFromPicker() {
      const root = this.getScopeRoot();
      if (!root) return null;
      const picker = root.querySelector(
        "variant-radios-detail, variant-group-detail, variant-radios"
      );
      return normalizeVariantId(picker?.currentVariant?.id);
    }

    connectedCallback() {
      this.select = this.querySelector('select[name="quantity"]');
      if (!this.select) return;

      this.select.addEventListener("change", (event) => {
        const synced = event?.detail?.jmQuantitySync === true;
        if (
          !this._applyingLimits &&
          !synced &&
          !(this._limits?.cartFull && this._limits?.showCartMaxHint)
        ) {
          this.clearLimitHint(true);
        }
        this.validateButtons();
        if (!synced) {
          this.syncSiblings();
        }
      });

      this.querySelectorAll(".quantity__button").forEach((button) => {
        button.addEventListener("click", (event) => this.onButtonClick(event));
      });

      this.validateButtons();
      queueMicrotask(() =>
        this.refreshFromActiveVariant({ useCart: false, showCartMaxHint: false })
      );
    }

    refreshFromActiveVariant(refreshOptions) {
      const root = this.getScopeRoot();
      if (!root) return;

      const picker = root.querySelector(
        "variant-radios-detail, variant-group-detail, variant-radios, variant-radios-sticky, variant-group-sticky"
      );
      const variant = picker?.currentVariant;
      if (!variant) return;

      const opts =
        typeof refreshOptions === "object" && refreshOptions !== null
          ? refreshOptions
          : { useCart: refreshOptions === true, showCartMaxHint: false };

      this.updateFromVariant(variant, root, {
        useCart: opts.useCart === true,
        showCartMaxHint: opts.showCartMaxHint === true,
      });
    }

    getQuantityState() {
      const values = Array.from(this.select?.options || []).map((o) =>
        parseInt(o.value, 10)
      );
      const current = parseInt(this.select?.value, 10);
      const idx = values.indexOf(current);
      return { values, current, idx, atMax: idx >= values.length - 1 };
    }

    onButtonClick(event) {
      event.preventDefault();
      const button = event.target.closest("button");
      if (!button || !this.select) return;

      const isPlus =
        button.name === "plus" || button.closest("button")?.name === "plus";
      const { values, current, idx, atMax } = this.getQuantityState();

      if (isPlus && (atMax || this._limits?.cartFull)) {
        this.showLimitHint();
        return;
      }
      if (idx < 0) return;

      const nextIdx = isPlus
        ? Math.min(idx + 1, values.length - 1)
        : Math.max(idx - 1, 0);

      if (values[nextIdx] !== current) {
        this.clearLimitHint();
        this.select.value = String(values[nextIdx]);
        this.select.dispatchEvent(this._changeEvent);
      }
    }

    validateButtons() {
      if (!this.select) return;
      const { idx, values } = this.getQuantityState();
      const minus = this.querySelector(".quantity__button[name='minus']");
      const plus = this.querySelector(".quantity__button[name='plus']");
      const atEnd =
        this._limits?.cartFull ||
        values.length === 0 ||
        idx >= values.length - 1;

      if (minus) {
        minus.classList.toggle("disabled", idx <= 0 || values.length === 0);
      }
      if (plus) {
        plus.classList.toggle("disabled", atEnd);
        plus.setAttribute("aria-disabled", atEnd ? "true" : "false");
      }
    }

    syncSiblings() {
      const value = this.select?.value;
      if (!value) return;
      const root = this.getScopeRoot() || document;
      root
        .querySelectorAll("jm-quantity-picker select[name='quantity']")
        .forEach((other) => {
          if (other !== this.select && other.value !== value) {
            const hasOption = Array.from(other.options).some(
              (o) => o.value === value
            );
            if (hasOption) other.value = value;
            other.dispatchEvent(
              new CustomEvent("change", {
                bubbles: true,
                detail: { jmQuantitySync: true },
              })
            );
          }
        });
    }

    ensureHintEl() {
      let hint = this.getHintEl();
      if (hint) return hint;

      const wrap = this.getWrap();
      if (!wrap) return null;

      hint = document.createElement("p");
      hint.className = "jm-quantity-picker__hint mb-0 fs-small";
      hint.setAttribute("role", "status");
      hint.setAttribute("aria-live", "polite");
      hint.hidden = true;
      wrap.appendChild(hint);
      return hint;
    }

    showLimitHint(persistent) {
      const text = limitMessage(this._limits);
      if (!text) return;

      const hint = this.ensureHintEl();
      if (!hint) return;

      hint.textContent = text;
      hint.hidden = false;
      hint.classList.add("jm-quantity-picker__hint--visible");

      const selectId = this.select?.id;
      if (selectId) {
        if (!hint.id) hint.id = `jm-qty-hint-${selectId}`;
        this.select.setAttribute("aria-describedby", hint.id);
      }

      if (persistent) {
        if (this._hintTimer) clearTimeout(this._hintTimer);
        this._hintTimer = null;
        return;
      }

      if (this._hintTimer) clearTimeout(this._hintTimer);
      this._hintTimer = setTimeout(() => this.clearLimitHint(), HINT_HIDE_MS);
    }

    clearLimitHint(force) {
      if (this._limits?.cartFull && this._limits?.showCartMaxHint && !force) {
        return;
      }
      if (this._hintTimer) {
        clearTimeout(this._hintTimer);
        this._hintTimer = null;
      }
      const hint = this.getHintEl();
      if (!hint) return;
      hint.hidden = true;
      hint.textContent = "";
      hint.classList.remove("jm-quantity-picker__hint--visible");
      this.select?.removeAttribute("aria-describedby");
    }

    setDebugAttributes(limits, row, resolvedVariantId) {
      if (!Shopify?.designMode) return;
      this.dataset.jmQtyVariantId = resolvedVariantId || "";
      this.dataset.jmQtyStock =
        row?.qty != null ? String(row.qty) : limits.stockTotal != null ? String(limits.stockTotal) : "";
      this.dataset.jmQtyInCart = String(limits.inCart || 0);
      this.dataset.jmQtyMax = String(limits.max);
      this.dataset.jmQtyRuleMax =
        limits.ruleMax != null ? String(limits.ruleMax) : "";
      this.dataset.jmQtyCartFull = limits.cartFull ? "true" : "false";
    }

    applyLimits(variant, limits, row) {
      if (!this.select) return;

      const { min, max, step, selectDisabled } = limits;
      this._applyingLimits = true;
      this._limits = limits;
      this.clearLimitHint(true);

      const prev = parseInt(this.select.value, 10) || min || 1;
      const values = buildValues(min, max, step);

      const fragment = document.createDocumentFragment();
      values.forEach((v) => {
        const opt = document.createElement("option");
        opt.value = String(v);
        opt.textContent = String(v);
        fragment.appendChild(opt);
      });

      this.select.replaceChildren(fragment);
      this.select.disabled = Boolean(selectDisabled);

      const clamped = values.includes(prev)
        ? prev
        : values.includes(min)
          ? min
          : values[values.length - 1];
      this.select.value = String(clamped);

      this.dataset.jmInventoryCapped = limits.inventoryCapped
        ? "true"
        : "false";
      this.dataset.jmMaxAvailable = String(max);
      this.dataset.jmCartFull = limits.cartFull ? "true" : "false";

      this.setDebugAttributes(
        limits,
        row,
        normalizeVariantId(variant?.id) || getPickerFormVariantId(this)
      );
      this.validateButtons();
      this.syncSiblings();
      this._applyingLimits = false;
    }

    updateFromVariant(variant, root, options) {
      if (!this.select || !variant) return;

      const scope = root || this.getScopeRoot();
      const useCart = options?.useCart === true;
      const showCartMaxHint = options?.showCartMaxHint === true;
      const ctx = resolveStockContext(variant, scope, this);
      const row = ctx.row;

      this.clearLimitHint(true);

      const applyWithInCart = (inCartQty) => {
        const limits = computeLimitsFromContext(ctx, {
          inCartQty,
          showCartMaxHint,
        });
        this.applyLimits(variant, limits, row);
        if (limits.showCartMaxHint && limits.cartFull) {
          this.showLimitHint(true);
        }
      };

      if (!useCart) {
        applyWithInCart(0);
        return;
      }

      const variantId = normalizeVariantId(variant.id);
      if (cartQtyByVariant) {
        applyWithInCart(getInCartQty(variantId));
        return;
      }

      fetchCartVariantQuantities().then(() => {
        applyWithInCart(getInCartQty(variantId));
      });
    }
  }

  JmQuantityPicker.computeLimits = computeLimits;
  JmQuantityPicker.enrichVariant = enrichVariantForQuantity;
  JmQuantityPicker.resolveStockContext = resolveStockContext;
  JmQuantityPicker.computeLimitsFromContext = computeLimitsFromContext;
  JmQuantityPicker.getVariantQtyRows = getVariantQtyRows;
  JmQuantityPicker.getQtyRow = getQtyRow;
  JmQuantityPicker.fetchCartVariantQuantities = fetchCartVariantQuantities;
  JmQuantityPicker.invalidateCartQtyCache = invalidateCartQtyCache;
  JmQuantityPicker.normalizeVariantId = normalizeVariantId;

  if (!customElements.get("jm-quantity-picker")) {
    customElements.define("jm-quantity-picker", JmQuantityPicker);
  }

  window.JmQuantityPicker = JmQuantityPicker;
  window.jmClearProductFormErrors = clearProductFormErrors;

  window.jmShowCartMaxMessage = function (root, message) {
    const text =
      message ||
      (window.variantStrings || {}).quantityInCartMax ||
      "";
    if (!text) return;
    const scope = root || document;
    let shown = false;
    scope.querySelectorAll("jm-quantity-picker").forEach((picker) => {
      picker._limits = {
        ...(picker._limits || {}),
        cartFull: true,
        showCartMaxHint: true,
      };
      if (typeof picker.showLimitHint === "function") {
        picker.showLimitHint(true);
        shown = true;
      }
    });
    if (!shown) {
      scope.querySelectorAll("product-form").forEach((form) => {
        if (typeof form.handleErrorMessage === "function") {
          form.handleErrorMessage(text);
        }
      });
    }
  };

  window.jmUpdateProductQuantityPickers = function (root, variant, options) {
    if (!root || !variant) return;
    clearProductFormErrors(root);
    root.querySelectorAll("jm-quantity-picker").forEach((el) => {
      if (typeof el.updateFromVariant === "function") {
        el.updateFromVariant(variant, root, options);
      }
    });
  };

  window.jmResetProductQuantityPickers = function (root) {
    if (!root) return;
    clearProductFormErrors(root);
    root.querySelectorAll("jm-quantity-picker").forEach((picker) => {
      if (picker._pendingUpdate) picker._pendingUpdate.cancelled = true;
      if (typeof picker.clearLimitHint === "function") {
        picker.clearLimitHint(true);
      }
      picker.dataset.jmCartFull = "false";
      if (picker.select) {
        picker.select.disabled = false;
      }
      picker._limits = {
        max: DEFAULT_MAX,
        inventoryCapped: false,
        cartFull: false,
        showCartMaxHint: false,
      };
      if (typeof picker.validateButtons === "function") {
        picker.validateButtons();
      }
    });
  };

  document.addEventListener("theme:cart-updated", () => {
    invalidateCartQtyCache();
    fetchCartVariantQuantities(true).then(() => {
      document.querySelectorAll("jm-quantity-picker").forEach((picker) => {
        if (typeof picker.refreshFromActiveVariant === "function") {
          picker.refreshFromActiveVariant({
            useCart: true,
            showCartMaxHint: false,
          });
        }
      });
    });
  });
})();
